import React, { memo } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { Box, Button, CircularProgress, Grid, Link } from '@mui/material'
import QUESTION_TYPE from '@v4/constants/questions'
import FormTextField from '@v4/components/common/Fields/FormTextField'
import FormCheckbox from '@v4/components/common/Fields/FormCheckbox'
import EditableHTML from '@v4/components/common/EditableHTML'
import Captcha from '@v4/components/common/Captcha'
import EditableContainer from '@v4/components/common/EditableContainer'
import { EDITOR_VARIANT } from '@v4/hooks/useEditor'
import Image from '@v4/components/common/Image/Image'
import AppThemeProvider from '@v4/components/common/AppThemeProvider'
import { Field as FormikField } from 'formik'
import ErrorMessage from '@v4/components/common/Fields/components/ErrorMessage'

import SettingsButton from '../SettingsButton'
import { useSignUpFormComponent } from './SignUpFormComponent.hooks'
import { REL_KEY } from './SignUpFormComponent.const'

const SignUpFormContent = props => {
	const [
		{
			classes,
			isSubmitting,
			className,
			buttonText,
			title,
			titleConfig,
			placeholderColor,
			usePaperColors,
			fieldsPerRow,
			firstNameLabel,
			firstnameEnabled,
			lastnameEnabled,
			lastNameLabel,
			emailLabel,
			emailEnabled,
			optinLabel,
			privacyWording,
			privacyPolicyUrl,
			data,
			divider,
			hasOptin,
			withHoneyPot,
			titleEditable,
			buttonTextEditable,
			buttonTextConfig,
			buttonTheme,
			buttonTextKey,
			titleKey,
			withSettings,
			settingsButtonVisible,
			getWording,
			white,
			withImage,
			image,
			captchaRef,
			withCaptcha,
		},
		{
			handleSubmit,
			handleTitleClick,
			handleButtonClick,
			handleTitleChange,
			handleButtonTextChange,
			handleSettingMouseEnter,
			handleSettingMouseLeave,
			handleImageClick,
		},
	] = useSignUpFormComponent(props)

	const titleContainer = (
		<EditableContainer
			visible={!!title}
			usePaperColors={usePaperColors}
			fieldConfig={titleConfig}
			onAdd={handleTitleChange}
			editable={titleEditable}
			white={white}
			wrapper={({ children }) => <div className={clsx(classes.titleWrap, 'form-text-container')}>{children}</div>}
		>
			<EditableHTML
				key={titleKey}
				className={clsx(classes.title, 'form-text-block-title')}
				gutterBottom
				html={title}
				usePaperColors={usePaperColors}
				editable={titleEditable}
				onClick={handleTitleClick}
				onChange={handleTitleChange}
				white={white}
			/>
		</EditableContainer>
	)

	return (
		<form name="signup" autoComplete="off" noValidate onSubmit={handleSubmit}>
			{withCaptcha && <Captcha ref={captchaRef} />}
			{withHoneyPot && (
				<FormCheckbox
					style={{ display: 'none' }}
					name={import.meta.env.VITE_HONEY_POT_FIELD}
					label="Label"
					usePaperColors={usePaperColors}
				/>
			)}
			{withImage ? (
				<div className={clsx(classes.header, 'form-header')}>
					<Image className={clsx(classes.image, 'form-image')} metaData={image} onClick={handleImageClick} />

					{titleContainer}
				</div>
			) : (
				titleContainer
			)}
			<div
				className={clsx(classes.wrapper, 'form-wrapper')}
				onMouseEnter={handleSettingMouseEnter}
				onMouseLeave={handleSettingMouseLeave}
			>
				{withSettings && (
					<SettingsButton
						className={clsx(
							classes.settingsButton,
							classes.topSettingsButton,
							settingsButtonVisible && classes.visible,
						)}
						label="Form settings"
					/>
				)}
				<Grid className={clsx(className, classes.root, 'form')} container>
					{firstnameEnabled && (
						<Grid
							className={clsx(classes.item, 'form-input-container')}
							size={{
								xs: 12,
								md: lastnameEnabled && !fieldsPerRow ? 6 : divider,
							}}
						>
							<FormTextField
								className={clsx(classes.input, classes[placeholderColor], 'form-input')}
								layout={placeholderColor}
								label={firstNameLabel}
								name="first_name"
								usePaperColors={usePaperColors}
								required
							/>
						</Grid>
					)}
					{lastnameEnabled && (
						<Grid
							className={clsx(classes.item, 'form-input-container')}
							size={{
								xs: 12,
								md: firstnameEnabled && !fieldsPerRow ? 6 : divider,
							}}
						>
							<FormTextField
								className={clsx(classes.input, classes[placeholderColor], 'form-input')}
								layout={placeholderColor}
								label={lastNameLabel}
								name="last_name"
								usePaperColors={usePaperColors}
								required
							/>
						</Grid>
					)}
					{emailEnabled && (
						<Grid
							className={clsx(classes.item, 'form-input-container')}
							size={{
								xs: 12,
								md: divider,
							}}
						>
							<FormTextField
								className={clsx(classes.input, classes[placeholderColor], 'form-input')}
								layout={placeholderColor}
								label={emailLabel}
								name="email"
								type="email"
								usePaperColors={usePaperColors}
								required
							/>
						</Grid>
					)}
					{_.map(data, ({ group, question: questionData }) => {
						const { answer_type: type, id, options = [], question, settings, required } = questionData

						switch (group) {
							case QUESTION_TYPE.CHECKBOX:
								return (
									<Grid
										className="form-checkbox-container"
										size={{
											md: 12,
											xs: 12,
										}}
									>
										<Grid container justifyContent="center" className="form-checkbox-row">
											<Grid
												className={clsx(classes.checkboxItem, 'form-checkbox-item')}
												size={{
													xs: 12,
													md: divider,
												}}
											>
												<FormCheckbox
													className={clsx(classes.checkbox, 'form-checkbox')}
													name={`questions.key_${id}`}
													label={question}
													type={type}
													usePaperColors={usePaperColors}
													required={required}
												/>
											</Grid>
										</Grid>
									</Grid>
								)
							case QUESTION_TYPE.OTHER: {
								const Field = REL_KEY[type]

								const InputProps =
									type === QUESTION_TYPE.DROPDOWN
										? { readOnly: true, margin: 'none' }
										: { margin: 'none' }

								return (
									<Grid
										key={id}
										className={clsx(classes.item, 'form-input-container')}
										size={{
											xs: 12,
											md: divider,
										}}
									>
										<Field
											className={clsx(
												type === QUESTION_TYPE.TEXT
													? [classes.input, classes[placeholderColor], 'form-input']
													: [classes.select, 'form-select'],
											)}
											required={required}
											layout={placeholderColor}
											name={`questions.key_${id}`}
											label={question}
											options={options}
											settings={settings}
											type={type}
											InputProps={InputProps}
											usePaperColors={usePaperColors}
										/>
									</Grid>
								)
							}
							default:
								return null
						}
					})}

					{hasOptin && (
						<Grid
							className="form-checkbox-container"
							size={{
								md: 12,
								xs: 12,
							}}
						>
							<Grid container justifyContent="center" className="form-checkbox-row">
								<Grid
									className={clsx(classes.checkboxItem, 'form-checkbox-item')}
									size={{
										xs: 12,
										md: divider,
									}}
								>
									<FormCheckbox
										className={clsx(classes.checkbox, 'form-checkbox')}
										name="opt_in"
										label={optinLabel || 'Opt in to receive updates via email'}
										usePaperColors={usePaperColors}
									/>
								</Grid>
							</Grid>
						</Grid>
					)}
				</Grid>
			</div>
			<Box pt={3} className="form-button-container">
				<Grid container justifyContent="center" spacing={3}>
					<Grid
						className={classes.buttonWrap}
						size={{
							xs: 12,
							md: divider,
						}}
					>
						<Button
							className={clsx(classes.button, 'form-button')}
							fullWidth
							type="submit"
							disabled={isSubmitting}
						>
							{isSubmitting && (
								<CircularProgress
									color="secondary"
									size={24}
									className={clsx(classes.buttonProgress, 'form-button-progress')}
								/>
							)}
							<AppThemeProvider theme={buttonTheme}>
								<EditableContainer
									visible={!!buttonText}
									usePaperColors={usePaperColors}
									fieldConfig={buttonTextConfig}
									onAdd={handleButtonTextChange}
									editable={buttonTextEditable}
									label="Button Text"
								>
									<EditableHTML
										key={buttonTextKey}
										className={clsx(classes.buttonText, 'form-text-block-button')}
										editable={buttonTextEditable}
										component="div"
										variant="button"
										html={buttonText}
										usePaperColors={usePaperColors}
										onClick={handleButtonClick}
										onChange={handleButtonTextChange}
										editorVariant={EDITOR_VARIANT.SIMPLE}
									/>
								</EditableContainer>
							</AppThemeProvider>
						</Button>
						<FormikField name="_error">
							{({ meta }) =>
								meta.error ? (
									<ErrorMessage className={classes.error} error>
										{meta.error}
									</ErrorMessage>
								) : null
							}
						</FormikField>
					</Grid>
				</Grid>
			</Box>
			{(privacyWording || privacyPolicyUrl) && (
				<Box
					mt={privacyWording ? 2 : 3}
					className={clsx(classes.wrapper, 'form-privacy-wrapper')}
					onMouseEnter={handleSettingMouseEnter}
					onMouseLeave={handleSettingMouseLeave}
				>
					{withSettings && (
						<SettingsButton
							className={clsx(classes.settingsButton, settingsButtonVisible && classes.visible)}
							label="Form settings"
						/>
					)}
					{privacyWording && (
						<Box display="flex" justifyContent="center" className="form-privacy-container">
							<EditableHTML
								className={clsx(classes.privacy, 'form-text-block-privacy')}
								variant="body1"
								align="center"
								display="block"
								html={privacyWording && `<p>${privacyWording}</p>`}
								usePaperColors={usePaperColors}
								white={white}
							/>
						</Box>
					)}
					{privacyPolicyUrl && (
						<Box
							className={clsx(classes.privacy, 'form-link-container')}
							mt={privacyWording ? 3 : 0}
							mx="auto"
						>
							<Link className={clsx(classes.link, 'form-link')} href={privacyPolicyUrl} target="_blank">
								<EditableHTML
									component="span"
									variant="body1"
									html={getWording('privacyPolicy')}
									usePaperColors={usePaperColors}
									className="form-text-block-link"
								/>
							</Link>
						</Box>
					)}
				</Box>
			)}
		</form>
	)
}

SignUpFormContent.propTypes = {
	className: PropTypes.string,
	classes: PropTypes.shape({
		root: PropTypes.string,
		checkboxes: PropTypes.string,
		item: PropTypes.string,
		checkboxItem: PropTypes.string,
		buttonProgress: PropTypes.string,
		optIn: PropTypes.string,
		button: PropTypes.string,
		buttonText: PropTypes.string,
		privacy: PropTypes.string,
		link: PropTypes.string,
		title: PropTypes.string,
		input: PropTypes.string,
		checkbox: PropTypes.string,
		select: PropTypes.string,
		buttonWrap: PropTypes.string,
	}),
	handleSubmit: PropTypes.func.isRequired,
	isSubmitting: PropTypes.bool.isRequired,
	settings: PropTypes.shape({
		optin: PropTypes.number,
		firstname_label: PropTypes.string,
		firstname_enabled: PropTypes.bool,
		lastname_enabled: PropTypes.bool,
		lastname_label: PropTypes.string,
		email_label: PropTypes.string,
		email_enabled: PropTypes.bool,
		optin_label: PropTypes.string,
		privacy_wording: PropTypes.string,
		privacy_policy_url: PropTypes.string,
	}),
	buttonText: PropTypes.string,
	title: PropTypes.string,
	titleConfig: PropTypes.object,
	placeholderColor: PropTypes.string,
	usePaperColors: PropTypes.bool,
	fieldsPerRow: PropTypes.oneOf([1, 2, 3, 4]),
	data: PropTypes.objectOf(
		PropTypes.arrayOf(
			PropTypes.shape({
				id: PropTypes.number,
				options: PropTypes.array,
				question: PropTypes.string,
				settings: PropTypes.object,
				answer_type: PropTypes.oneOf(Object.values(QUESTION_TYPE)),
			}),
		),
	),
	withOptin: PropTypes.bool,
	withCaptcha: PropTypes.bool,
	titleEditable: PropTypes.bool,
	onTitleClick: PropTypes.func,
	onButtonClick: PropTypes.func,
	onTitleChange: PropTypes.func,
	buttonTextEditable: PropTypes.bool,
	buttonTextConfig: PropTypes.object,
	onButtonTextChange: PropTypes.func,
	titleKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	buttonTextKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	rounded: PropTypes.bool,
	mobileAlignment: PropTypes.string,
	mobileButtonWidth: PropTypes.string,
	white: PropTypes.bool,
}

export default memo(SignUpFormContent)
